import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { fmtMoney, fmtMonth, fmtNumber } from '../lib/format';
import type { GoalItem, GoalMetric, GoalPeriod, Goals } from '../lib/types';
import { Button, Dialog, ErrorState, Field, Input, SkeletonRows, useConfirm, useToast } from '../components/ui';

const LABEL: Record<GoalMetric, string> = { sales: 'מכירות (לפני מע״מ)', collected: 'גבייה — כסף שנכנס', new_buyers: 'לקוחות חדשים שהזמינו' };
const HINT: Record<GoalMetric, string> = {
  sales: 'חשבוניות בניכוי זיכויים, כמו במאגר החשבוניות',
  collected: 'קבלות, חשבוניות מס/קבלה וחשבוניות שסומנו כשולמו',
  new_buyers: 'לקוחות שההזמנה הראשונה שלהם בתקופה',
};
const isMoney = (m: GoalMetric) => m !== 'new_buyers';
const fmt = (m: GoalMetric, v: number | null | undefined) => (isMoney(m) ? fmtMoney(v, { round: true }) : fmtNumber(v ?? 0));

export function GoalsPage() {
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ['goals'], queryFn: () => api<Goals>('/api/goals') });
  const [editing, setEditing] = useState<{ period: GoalPeriod; item: GoalItem } | null>(null);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>יעדים</h1>
          <div className="sub">יעדי מכירות, גבייה ולקוחות חדשים — עם צפי לסוף התקופה לפי הקצב עד היום.</div>
        </div>
      </div>
      {editing && data ? <GoalDialog period={editing.period} item={editing.item} suggestions={data.suggestions} onClose={() => setEditing(null)} /> : null}
      {error ? (
        <div className="panel"><ErrorState error={error} onRetry={() => refetch()} /></div>
      ) : !data ? (
        <div className="panel"><table className="table"><tbody><SkeletonRows rows={4} cols={4} /></tbody></table></div>
      ) : (
        <div className="stack">
          <PeriodPanel title={`החודש — ${fmtMonth(data.month.start.slice(0, 7))}`} period={data.month} onEdit={(item) => setEditing({ period: data.month, item })} loading={isLoading} />
          <PeriodPanel title={`השנה — ${data.year.start.slice(0, 4)}`} period={data.year} onEdit={(item) => setEditing({ period: data.year, item })} loading={isLoading} />
          <History data={data} />
        </div>
      )}
    </>
  );
}

function PeriodPanel({ title, period, onEdit, loading }: { title: string; period: GoalPeriod; onEdit: (i: GoalItem) => void; loading: boolean }) {
  const pace = Math.round((period.elapsedDays / period.totalDays) * 100);
  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{title}</h2>
        <span className="spacer" />
        <span className="small muted">עברו {period.elapsedDays} מתוך {period.totalDays} ימים ({pace}%)</span>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>מדד</th>
              <th className="num">בפועל</th>
              <th className="num">יעד</th>
              <th style={{ width: '32%' }}>התקדמות</th>
              <th className="num">צפי לסוף התקופה</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading ? <SkeletonRows rows={3} cols={6} /> : null}
            {period.items.map((i) => (
              <tr key={i.metric}>
                <td>
                  <div className="strong">{LABEL[i.metric]}</div>
                  <div className="sub">{i.note ?? HINT[i.metric]}</div>
                </td>
                <td className="num strong">{fmt(i.metric, i.actual)}</td>
                <td className="num">{i.target != null ? fmt(i.metric, i.target) : <span className="faint">—</span>}</td>
                <td>
                  {i.target != null ? (
                    <>
                      <div className={`progress ${i.onTrack === false ? 'behind' : ''}`} title={`${i.pct}% מהיעד · הקו מסמן את הקצב הנדרש עד היום`}>
                        <span style={{ width: `${Math.min(100, i.pct ?? 0)}%` }} />
                        <i className="pace" style={{ insetInlineStart: `${pace}%` }} />
                      </div>
                      <div className="small muted" style={{ marginTop: 4 }}>
                        {i.pct}%{i.onTrack === false && i.neededPerDay != null ? ` · נדרש ${fmt(i.metric, i.neededPerDay)} ליום` : i.onTrack ? ' · בקצב' : ''}
                      </div>
                    </>
                  ) : (
                    <span className="small muted">לא הוגדר יעד</span>
                  )}
                </td>
                <td className="num" style={i.onTrack === false ? { color: 'var(--warn)' } : i.onTrack ? { color: 'var(--ok)' } : undefined}>{fmt(i.metric, i.projected)}</td>
                <td className="num">
                  <Button size="sm" variant={i.target == null ? 'primary' : 'ghost'} onClick={() => onEdit(i)}>{i.target == null ? 'הגדרת יעד' : 'עריכה'}</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function History({ data }: { data: Goals }) {
  const rows = data.history.filter((h) => (h.sales ?? 0) !== 0 || (h.collected ?? 0) !== 0);
  const max = Math.max(1, ...rows.map((h) => Math.max(h.sales ?? 0, h.salesTarget ?? 0)));
  return (
    <section className="panel">
      <div className="panel-head">
        <h2>12 החודשים האחרונים</h2>
        <span className="spacer" />
        <span className="small muted">עוזר לקבוע יעד מציאותי</span>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>חודש</th>
              <th style={{ width: '38%' }}>מכירות</th>
              <th className="num">מכירות לפני מע״מ</th>
              <th className="num hide-sm">יעד</th>
              <th className="num hide-sm">גבייה</th>
              <th className="num hide-sm">לקוחות חדשים</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((h) => (
              <tr key={h.month}>
                <td className="nowrap">{fmtMonth(h.month)}</td>
                <td>
                  <div className="bar-track" style={{ width: `${Math.max(3, ((h.sales ?? 0) / max) * 100)}%` }}>
                    <div className="paid" style={{ width: '100%' }} />
                  </div>
                </td>
                <td className="num strong">{fmtMoney(h.sales, { round: true })}</td>
                <td className="num hide-sm">{h.salesTarget != null ? fmtMoney(h.salesTarget, { round: true }) : <span className="faint">—</span>}</td>
                <td className="num hide-sm">{fmtMoney(h.collected, { round: true })}</td>
                <td className="num hide-sm">{h.newBuyers}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function GoalDialog({ period, item, suggestions, onClose }: { period: GoalPeriod; item: GoalItem; suggestions: Goals['suggestions']; onClose: () => void }) {
  const [target, setTarget] = useState(item.target != null ? String(item.target) : '');
  const [note, setNote] = useState(item.note ?? '');
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const monthly = item.metric === 'sales' ? suggestions.sales : item.metric === 'collected' ? suggestions.collected : suggestions.newBuyers;
  const suggestion = monthly != null && monthly > 0 ? Math.round(period.period === 'year' ? monthly * 12 : monthly) : null;
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['goals'] });
    qc.invalidateQueries({ queryKey: ['workplan'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  };
  const save = async () => {
    setBusy(true);
    try {
      await api('/api/goals', { method: 'PUT', body: { metric: item.metric, period: period.period, periodStart: period.start, target: Number(target), note: note.trim() || null } });
      refresh();
      toast.success('היעד נשמר');
      onClose();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!item.goalId || !(await confirm({ title: 'למחוק את היעד?', confirmLabel: 'מחיקה', danger: true }))) return;
    try {
      await api(`/api/goals/${item.goalId}`, { method: 'DELETE' });
      refresh();
      onClose();
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`יעד ${period.period === 'month' ? 'חודשי' : 'שנתי'}: ${LABEL[item.metric]}`}
      footer={
        <>
          <Button variant="primary" onClick={save} loading={busy} disabled={!(Number(target) > 0)}>שמירה</Button>
          <Button onClick={onClose}>ביטול</Button>
          {item.goalId ? <Button variant="ghost" onClick={remove}>מחיקת יעד</Button> : null}
        </>
      }
    >
      <div className="stack">
        <Field label={isMoney(item.metric) ? 'יעד (₪)' : 'יעד (מספר לקוחות)'} required>
          <Input type="number" min={1} value={target} onChange={(e) => setTarget(e.target.value)} ltr autoFocus />
        </Field>
        {suggestion ? (
          <p className="small muted">
            ממוצע 3 החודשים האחרונים{period.period === 'year' ? ' × 12' : ''}: <button type="button" className="link-button" onClick={() => setTarget(String(suggestion))}>{fmt(item.metric, suggestion)}</button>
          </p>
        ) : null}
        <Field label="הערה (לא חובה)">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="למשל: כולל פרויקט העירייה" />
        </Field>
      </div>
    </Dialog>
  );
}
